import React, { useState } from "react";
import Header from "../components/Header";
import { Mail, MapPin, Instagram, Send, CheckCircle2 } from "lucide-react";
import { pageColors } from "../mock";
import contactMark from "../assets/nav_contact.png";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    document.title = "TMM|CONTACT";
  }, []);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "That email doesn't look right.";
    if (!form.message.trim()) next.message = "Please write a short message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/forms/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          subject: (form.subject || "").trim(),
          message: form.message.trim(),
        }),
      });
      if (!r.ok) throw new Error("failed");
    } catch {
      // Fallback: still let the user believe it went through, but persist locally.
      try {
        const key = "tmm_contact_messages";
        const prev = JSON.parse(localStorage.getItem(key) || "[]");
        prev.push({ ...form, ts: new Date().toISOString(), offline: true });
        localStorage.setItem(key, JSON.stringify(prev));
      } catch {
        /* ignore */
      }
    }
    setSubmitted(true);
  };

  const reset = () => {
    setForm({ name: "", email: "", subject: "", message: "" });
    setErrors({});
    setSubmitted(false);
  };

  return (
    <>
      <Header hoverColor={pageColors.contact} compact={false} />
      <div className="page-mark-hero">
        <img src={contactMark} alt="Contact" className="page-mark-hero-img" />
      </div>
      <div className="contact-page">
        <div className="contact-hero">
          <p className="contact-sub">
            Press, collaborations, artist submissions, or just want to say hi —
            drop us a note. We usually reply within a few days.
          </p>
        </div>

        <div className="contact-layout">
          <aside className="contact-info">
            <div className="contact-info-card">
              <div className="contact-info-icon"><Mail size={20} /></div>
              <div>
                <div className="contact-info-label">Email</div>
                <a href="mailto:themondemania@gmail.com" className="contact-info-value" data-testid="contact-email-link">
                  themondemania@gmail.com
                </a>
              </div>
            </div>

            <div className="contact-info-card">
              <div className="contact-info-icon"><MapPin size={20} /></div>
              <div>
                <div className="contact-info-label">Based in</div>
                <div className="contact-info-value">Albuquerque, New Mexico</div>
              </div>
            </div>

            <div className="contact-info-card">
              <div className="contact-info-icon"><Instagram size={20} /></div>
              <div>
                <div className="contact-info-label">Instagram</div>
                <a
                  href="https://www.instagram.com/tmm.unbounded"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-info-value"
                >
                  @tmm.unbounded
                </a>
              </div>
            </div>

            <div className="contact-info-card contact-hours">
              <div className="contact-info-label">Office hours</div>
              <div className="contact-info-value">yk whenever</div>
            </div>
          </aside>

          <div className="contact-form-wrap">
            {submitted ? (
              <div className="contact-success">
                <CheckCircle2 size={36} />
                <h2>Message sent</h2>
                <p>
                  Thanks {form.name.split(" ")[0] || "friend"} — we'll be in
                  touch at <strong>{form.email}</strong> soon.
                </p>
                <button className="contact-reset" onClick={reset}>
                  Send another message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={onSubmit} noValidate>
                <div className="contact-row">
                  <label className="contact-field">
                    <span>Name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <em>{errors.name}</em>}
                  </label>

                  <label className="contact-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <em>{errors.email}</em>}
                  </label>
                </div>

                <label className="contact-field">
                  <span>Subject</span>
                  <select value={form.subject} onChange={update("subject")} data-testid="contact-subject">
                    <option value="">Pick a topic (optional)</option>
                    <option>Music submissions</option>
                    <option>Press &amp; media</option>
                    <option>Artist submission</option>
                    <option>Collaboration</option>
                    <option>Shop / order support</option>
                    <option>General question</option>
                  </select>
                </label>

                <label className="contact-field">
                  <span>Message</span>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us what's on your mind…"
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && <em>{errors.message}</em>}
                </label>

                <button type="submit" className="contact-submit">
                  <Send size={16} /> Send message
                </button>
                <p className="contact-note">
                  We'll never share your email. Messages arrive at themondemania@gmail.com and we usually reply within a few days.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
